import React, { useCallback, useEffect, useRef, useState } from "react";

export default function AudioPlayer({ file, src, className = "" }) {
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const peaksRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [isPlaying, setPlaying] = useState(false);
  const [loop, setLoop] = useState(false);
  const [rate, setRate] = useState(1.0);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    let u = src || null;
    if (file) u = URL.createObjectURL(file);
    setUrl(u);
    return () => { if (file && u) URL.revokeObjectURL(u); };
  }, [file, src]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const w = (canvas.width  = canvas.offsetWidth || 600);
    const h = (canvas.height = canvas.offsetHeight || 40);

    ctx.clearRect(0, 0, w, h);

    // baseline
    ctx.globalAlpha = 0.6;
    ctx.fillStyle = "#1f2638";
    ctx.fillRect(0, Math.floor(h / 2) - 1, w, 2);

    // waveform
    const peaks = peaksRef.current;
    if (peaks?.length) {
      const step = w / peaks.length;
      ctx.globalAlpha = 1;
      ctx.strokeStyle = "#475569";
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let i = 0; i < peaks.length; i++) {
        const x = i * step + 0.5;
        const amp = Math.max(2, Math.floor(peaks[i] * (h / 2 - 4)));
        ctx.moveTo(x, h / 2 - amp);
        ctx.lineTo(x, h / 2 + amp);
      }
      ctx.stroke();
    }

    // progress
    const p = duration ? current / duration : 0;
    if (p > 0) {
      ctx.fillStyle = "#8b5cf6";
      ctx.globalAlpha = 0.25;
      ctx.fillRect(0, 0, Math.max(2, Math.floor(w * p)), h);
    }
  }, [current, duration]);

  // Audio events
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onLoaded = () => setDuration(a.duration || 0);
    const onTime   = () => setCurrent(a.currentTime || 0);
    const onEnded  = () => setPlaying(false);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnded);
    };
  }, [url]);

  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.time;
      const a = audioRef.current;
      if (!a || typeof t !== 'number' || !isFinite(t)) return;
      a.currentTime = Math.max(0, Math.min(t, duration || a.duration || t));
      a.playbackRate = rate; a.loop = loop;
      a.play().then(() => setPlaying(true)).catch(() => {});
    };
   window.addEventListener('seek-audio', handler);
   return () => window.removeEventListener('seek-audio', handler);
 }, [duration, rate, loop]);

  // Build peaks (if local file)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!file) { peaksRef.current = null; draw(); return; }
      try {
        const arrBuf = await file.arrayBuffer();
        const Ctx = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!Ctx) { peaksRef.current = null; draw(); return; }
        const ac  = new Ctx(1, 44100, 44100);
        const buf = await ac.decodeAudioData(arrBuf.slice(0));
        const data = buf.getChannelData(0);
        const samples = 800;
        const block = Math.max(1, Math.floor(data.length / samples));
        const peaks = new Array(samples);
        for (let i = 0; i < samples; i++) {
          let sum = 0, start = i * block;
          for (let j = 0; j < block; j++) sum += Math.abs(data[start + j] || 0);
          peaks[i] = sum / block;
        }
        if (!cancelled) { peaksRef.current = peaks; draw(); }
      } catch { peaksRef.current = null; draw(); }
    })();
    return () => { cancelled = true; };
  }, [file, draw]);

  // Redraw whenever draw's deps change (current/duration)
  useEffect(() => { draw(); }, [draw, url]);

  // Redraw on resize (for mobile wraps/orientation)
  useEffect(() => {
    const el = canvasRef.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver(() => draw());
    ro.observe(el);
    return () => ro.disconnect();
  }, [draw]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) { a.pause(); setPlaying(false); }
    else { a.playbackRate = rate; a.loop = loop; a.play(); setPlaying(true); }
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const p = Math.min(1, Math.max(0, x / rect.width));
    const a = audioRef.current;
    if (a && duration) a.currentTime = Math.max(0, duration * p);
  };

  const skip = (sec) => {
    const a = audioRef.current; if (!a) return;
    a.currentTime = Math.max(0, Math.min((a.currentTime || 0) + sec, duration));
  };

  const fmt = (t) => {
    if (!isFinite(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className={`rounded-xl border border-[#1f2638] bg-[#0e1320]/60 p-2 sm:p-3 ${className}`}>
      <audio ref={audioRef} src={url || undefined} preload="metadata" className="hidden" />

      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {/* Controls */}
        <div className="flex items-center gap-2 order-1">
          <button className="btn btn-ghost btn-sm sm:btn-md" onClick={toggle} aria-label={isPlaying ? "Пауза" : "Пусни"} title={isPlaying ? "Пауза" : "Пусни"}>
            {isPlaying ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            )}
          </button>
          <button className="btn btn-ghost btn-sm sm:btn-md" onClick={() => skip(-5)} title="Назад 5 сек">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 19A8 8 0 1 1 19 8" /><path d="M7 10H3v4" /><path d="M7 10l-4 4" />
            </svg>
          </button>
          <button className="btn btn-ghost btn-sm sm:btn-md" onClick={() => skip(5)} title="Напред 5 сек">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 5A8 8 0 1 1 5 16" /><path d="M17 14h4v-4" /><path d="M17 14l4-4" />
            </svg>
          </button>
        </div>

        {/* Time */}
        <div className="order-2 text-right text-xs sm:text-sm text-slate-300 tabular-nums w-[74px] sm:w-[92px]">
          {fmt(current)} / {fmt(duration)}
        </div>

        {/* Speed */}
        <div className="order-3">
          <div className="relative">
            <select
              aria-label="Скорост"
              className="audio-select appearance-none text-xs sm:text-sm h-8 px-2 pr-8 rounded-md
                         border border-[#1f2638] bg-[#0e1320] text-slate-200
                         focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-400"
              value={rate}
              onChange={(e) => {
                const v = Number(e.target.value);
                setRate(v);
                if (audioRef.current) audioRef.current.playbackRate = v;
              }}
              title="Скорост"
            >
              <option value={0.75}>0.75×</option>
              <option value={1}>1.0×</option>
              <option value={1.25}>1.25×</option>
              <option value={1.5}>1.5×</option>
              <option value={2}>2.0×</option>
            </select>
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 opacity-70">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M7 10l5 5 5-5H7z" /></svg>
            </span>
          </div>
        </div>

        {/* Loop */}
        <div className="order-4">
          <button
            className={`btn btn-ghost btn-sm sm:btn-md ${loop ? "text-violet-400" : ""}`}
            onClick={() => { const next = !loop; setLoop(next); if (audioRef.current) audioRef.current.loop = next; }}
            title="Повтаряй" aria-pressed={loop}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 7h13a4 4 0 0 1 4 4v1" />
              <path d="M7 3L3 7l4 4" />
              <path d="M21 17H8a4 4 0 0 1-4-4v-1" />
              <path d="M17 21l4-4-4-4" />
            </svg>
          </button>
        </div>

        {/* Waveform */}
        <div className="order-5 basis-full sm:order-none sm:flex-1">
          <div
            className="h-12 sm:h-10 rounded-lg overflow-hidden cursor-pointer"
            onClick={seek}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={current || 0}
          >
            <canvas ref={canvasRef} className="block w-full h-12 sm:h-10" />
          </div>
        </div>
      </div>
    </div>
  );
}
